import React, { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { graphql } from 'react-relay';

import Button from '@atlaskit/button/new';
import AiGenerativeTextIcon from '@atlaskit/icon-lab/core/ai-generative-text';
import { token } from '@atlaskit/tokens';

import { useLazyQueryPromiseInterop } from '@confluence/apollo-relay-interop/entry-points/useLazyQueryPromiseInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { createStagedSmartCreateDraft } from './utils/createStagedSmartCreateDraft.ts';
import { useOpenModalWithTemplate } from './hooks/useOpenModalWithTemplate.ts';

import type {
	CreateWithRovoTemplateButtonTemplatesQuery$data,
	CreateWithRovoTemplateButtonTemplatesQuery$variables,
} from './__generated__/CreateWithRovoTemplateButtonTemplatesQuery.graphql.ts';

export const i18n = defineMessages({
	createWithRovoButton: {
		id: 'smart-create.create-with-rovo-template-button',
		defaultMessage: 'Create with Rovo',
		description: 'Text for the button to create from a template with Rovo',
	},
});

const CreateWithRovoTemplateButtonTemplatesQuery = graphql`
	query CreateWithRovoTemplateButtonTemplatesQuery($spaceKey: String!, $ids: [String!]!) {
		templateBodies(spaceKey: $spaceKey, ids: $ids) {
			nodes {
				id
				body {
					value
					representation
				}
			}
		}
	}
`;

/**
 * Session-level cache for template ADF bodies keyed by `${spaceKey}:${templateId}`.
 * Avoids re-calling `useLazyQueryPromiseInterop` on subsequent clicks — Apollo cache hits
 * don't change the `data` ref, so the hook's useEffect never re-fires and the promise hangs.
 */
export const templateBodiesCache = new Map<string, string>();

type Template = {
	contentBlueprintId?: string | null;
	templateId?: string | null;
	itemModuleCompleteKey?: string | null;
	name?: string | null;
};

type CreateWithRovoTemplateButtonProps = {
	template: Template;
	spaceKey: string;
	onModalOpen?: () => void;
};

const getTemplateIdForQuery = (template: Template) => {
	return (template.contentBlueprintId || template.templateId || template.itemModuleCompleteKey)!;
};

export const CreateWithRovoTemplateButton = ({
	template,
	spaceKey,
	onModalOpen,
}: CreateWithRovoTemplateButtonProps) => {
	const intl = useIntl();
	const { cloudId } = useSessionData();
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const fetchTemplateBodies = useLazyQueryPromiseInterop<
		CreateWithRovoTemplateButtonTemplatesQuery$data,
		CreateWithRovoTemplateButtonTemplatesQuery$variables
	>(CreateWithRovoTemplateButtonTemplatesQuery);
	const openModalWithTemplate = useOpenModalWithTemplate({
		assistanceServiceParams: {
			product: 'confluence',
			experienceId: 'ai-mate',
			touchpointSource: 'template-gallery-smart-create',
			siteId: cloudId ?? '',
		},
	});

	const fetchTemplateAdf = useCallback(async (): Promise<string | undefined> => {
		const templateId = getTemplateIdForQuery(template);
		const cacheKey = `${spaceKey}:${templateId}`;

		const cachedTemplateBody = templateBodiesCache.get(cacheKey);
		if (cachedTemplateBody !== undefined) {
			return cachedTemplateBody;
		}

		const templateBodiesData = await fetchTemplateBodies({
			variables: { spaceKey, ids: [templateId] },
		});
		const templateNode = templateBodiesData?.templateBodies?.nodes?.find(
			(node) => node?.id === templateId,
		);

		const { value: adfBody, representation } = templateNode?.body ?? {};

		if (!adfBody || representation !== 'atlas_doc_format') {
			return undefined;
		}

		templateBodiesCache.set(cacheKey, adfBody);
		return adfBody;
	}, [fetchTemplateBodies, spaceKey, template]);

	const handleClick = useCallback(async () => {
		setIsLoading(true);
		try {
			if (!cloudId) {
				// TODO Add error handling https://hello.jira.atlassian.cloud/browse/AIFC-1490
				setIsLoading(false);
				return;
			}

			const stagedResponse = await createStagedSmartCreateDraft(cloudId);
			const contentId = stagedResponse.contentId;

			if (!contentId) {
				// TODO Add error handling https://hello.jira.atlassian.cloud/browse/AIFC-1490
				setIsLoading(false);
				return;
			}

			const adfBody = await fetchTemplateAdf();

			if (!adfBody) {
				// TODO Add error handling https://hello.jira.atlassian.cloud/browse/AIFC-1490
				setIsLoading(false);
				return;
			}

			await openModalWithTemplate({
				contentId,
				adfBody,
				templateName: template.name || '',
				spaceKey,
				onOpenCallback: () => {
					onModalOpen?.();
					setIsLoading(false);
				},
			});
		} catch (error) {
			// TODO Add error handling https://hello.jira.atlassian.cloud/browse/AIFC-1490
			setIsLoading(false);
			return error;
		}
	}, [cloudId, template, spaceKey, fetchTemplateAdf, openModalWithTemplate, onModalOpen]);

	return (
		<Button
			appearance="default"
			iconBefore={(iconProps) => (
				<AiGenerativeTextIcon {...iconProps} color={token('color.icon')} />
			)}
			onClick={handleClick}
			isLoading={isLoading}
			testId="create-with-rovo-button"
		>
			{intl.formatMessage(i18n.createWithRovoButton)}
		</Button>
	);
};
